import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import { contactosCollectionUrl } from '../../contacto.reducer';
import {
  CORREO_CONTACTO_PRIMARIO,
  CORREO_CONTACTO_SECUNDARIO,
  parseProveedorContactoCorreo,
  type ProveedorContactoCorreo,
  type ProveedorEmpresaConfigApi,
} from './proveedor-config-preview';

export type { ProveedorContactoCorreo };
export type ProveedorContactoSiNo = 'SI' | 'NO';

export type ProveedorContactoEntry = {
  contacto: Record<string, unknown>;
  correo: ProveedorContactoCorreo;
  repDocs: ProveedorContactoSiNo;
  recordatorio: ProveedorContactoSiNo;
};

export type GrupoContactoCatalogItem = Record<string, unknown> & {
  id?: number;
  contacto?: string;
  nombre?: string;
  apellido?: string;
};

const CORREO_OPTIONS: { value: ProveedorContactoCorreo; label: string }[] = [
  { value: CORREO_CONTACTO_PRIMARIO, label: 'Primario' },
  { value: CORREO_CONTACTO_SECUNDARIO, label: 'Secundario' },
];

const SI_NO_OPTIONS: { value: ProveedorContactoSiNo; label: string }[] = [
  { value: 'SI', label: 'SI' },
  { value: 'NO', label: 'NO' },
];

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '4px 6px',
  fontSize: 12,
  border: '1px solid var(--crm-border)',
  borderRadius: 6,
  background: 'var(--crm-input-bg)',
  color: 'var(--crm-text-primary)',
};

const pickerSelectStyle: React.CSSProperties = {
  ...selectStyle,
  minWidth: 160,
  flex: 1,
};

function contactoLabel(c: GrupoContactoCatalogItem | Record<string, unknown>): string {
  const label = String(c.contacto ?? '').trim();
  if (label) return label;
  const nombre = `${c.nombre ?? ''} ${c.apellido ?? ''}`.trim();
  return nombre || `ID ${c.id}`;
}

function parseSiNo(v: unknown): ProveedorContactoSiNo {
  if (v === true || v === 'SI' || v === 'si' || v === 'S' || v === 'YES') return 'SI';
  if (v === false || v === 'NO' || v === 'no' || v === 'N') return 'NO';
  return 'NO';
}


export function parseProveedorEmpresaContactos(
  config: ProveedorEmpresaConfigApi | null,
): ProveedorContactoEntry[] {
  const raw = config?.contactos;
  if (!Array.isArray(raw)) return [];
  const out: ProveedorContactoEntry[] = [];
  const seen = new Set<number>();
  for (const item of raw) {
    if (item == null || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const contacto = row.contacto;
    if (contacto == null || typeof contacto !== 'object' || Array.isArray(contacto)) continue;
    const c = contacto as Record<string, unknown>;
    if (c.id == null) continue;
    const id = Number(c.id);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({
      contacto: c,
      correo: parseProveedorContactoCorreo(c, row.correo),
      repDocs: parseSiNo(row.repDocs ?? row.representanteDocumentos),
      recordatorio: parseSiNo(row.recordatorio),
    });
  }
  return out;
}

export default function ProveedorContactosSection({
  grupoId,
  contactos,
  onChange,
  disabled = false,
}: {
  grupoId: string | number | null;
  contactos: ProveedorContactoEntry[];
  onChange: (next: ProveedorContactoEntry[]) => void;
  disabled?: boolean;
}) {
  const [catalog, setCatalog] = useState<GrupoContactoCatalogItem[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedContactoId, setSelectedContactoId] = useState('');

  useEffect(() => {
    if (grupoId == null || grupoId === '') {
      setCatalog([]);
      return;
    }
    let cancelled = false;
    setCatalogLoading(true);
    axios
      .get<unknown>(contactosCollectionUrl(grupoId), {
        params: { page: 0, size: 99999, sort: 'id,asc' },
      })
      .then(res => {
        if (cancelled) return;
        const data = res.data;
        const list = Array.isArray(data)
          ? data
          : (data as { content?: unknown[] })?.content ?? (data as { data?: unknown[] })?.data ?? [];
        setCatalog(Array.isArray(list) ? (list as GrupoContactoCatalogItem[]) : []);
      })
      .catch(() => {
        if (!cancelled) setCatalog([]);
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [grupoId]);

  const assignedIds = useMemo(
    () => new Set(contactos.map(c => Number(c.contacto?.id)).filter(Number.isFinite)),
    [contactos],
  );

  const catalogById = useMemo(() => {
    const map = new Map<number, GrupoContactoCatalogItem>();
    for (const c of catalog) {
      if (c.id != null) map.set(Number(c.id), c);
    }
    return map;
  }, [catalog]);

  const contactosDisponibles = useMemo(
    () => catalog.filter(c => c.id != null && !assignedIds.has(Number(c.id))),
    [catalog, assignedIds],
  );

  useEffect(() => {
    if (selectedContactoId === '') return;
    const id = Number(selectedContactoId);
    if (assignedIds.has(id)) setSelectedContactoId('');
  }, [assignedIds, selectedContactoId]);

  const updateRowByContactoId = (contactoId: number, patch: Partial<ProveedorContactoEntry>) => {
    if (disabled) return;
    onChange(
      contactos.map(row =>
        Number(row.contacto?.id) === contactoId ? { ...row, ...patch } : row,
      ),
    );
  };

  const handleRemoveRow = (contactoId: number) => {
    if (disabled) return;
    onChange(contactos.filter(c => Number(c.contacto?.id) !== contactoId));
  };

  const handleAdd = () => {
    if (disabled) return;
    const id = Number(selectedContactoId);
    if (!Number.isFinite(id) || assignedIds.has(id)) return;
    const found = catalogById.get(id);
    if (!found) return;
    onChange([
      ...contactos,
      {
        contacto: found,
        correo: CORREO_CONTACTO_PRIMARIO,
        repDocs: 'NO',
        recordatorio: 'NO',
      },
    ]);
    setSelectedContactoId('');
  };

  const puedeAgregar =
    !disabled &&
    selectedContactoId !== '' &&
    !assignedIds.has(Number(selectedContactoId)) &&
    !catalogLoading &&
    grupoId != null &&
    grupoId !== '';

  return (
    <section
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minHeight: 0,
      }}
      aria-labelledby="proveedor-contactos-section-title"
    >
      <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
        <h3
          id="proveedor-contactos-section-title"
          className="m-0"
          style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}
        >
          Contactos
        </h3>
        <div className="d-flex align-items-center gap-2" style={{ flex: '1 1 200px', maxWidth: 360 }}>
          <select
            style={pickerSelectStyle}
            value={selectedContactoId}
            onChange={e => setSelectedContactoId(e.target.value)}
            disabled={disabled || catalogLoading || contactosDisponibles.length === 0}
            aria-label="Seleccionar contacto"
          >
            <option value="">
              {catalogLoading
                ? 'Cargando…'
                : contactosDisponibles.length === 0
                  ? 'Sin contactos disponibles'
                  : '— Contacto —'}
            </option>
            {contactosDisponibles.map(c => (
              <option key={c.id} value={String(c.id)}>
                {contactoLabel(c)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-sm btn-light border d-inline-flex align-items-center gap-1 flex-shrink-0"
            onClick={handleAdd}
            disabled={!puedeAgregar}
          >
            <FaPlus size={11} />
            Agregar
          </button>
        </div>
      </div>

      {catalogLoading ? (
        <p className="m-0" style={{ fontSize: 12, color: '#64748b' }}>
          Cargando contactos del grupo…
        </p>
      ) : null}

      {!catalogLoading && catalog.length === 0 ? (
        <p className="m-0" style={{ fontSize: 12, color: '#94a3b8' }}>
          No hay contactos en el grupo.
        </p>
      ) : (
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table className="table tabla-chica m-0" style={{ minWidth: 320, fontSize: 12 }}>
            <thead>
              <tr className="bg-primary">
                <th>Contacto</th>
                <th>Correo</th>
                <th>Rep. Docs</th>
                <th>Recordatorio</th>
                <th style={{ width: 32 }} aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {contactos.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                    Seleccione un contacto y pulse Agregar.
                  </td>
                </tr>
              ) : (
                contactos.map(row => {
                  const contactoId = Number(row.contacto?.id);
                  return (
                    <tr key={contactoId}>
                      <td style={{ minWidth: 120, verticalAlign: 'middle' }}>
                        {contactoLabel(row.contacto)}
                      </td>
                      <td style={{ minWidth: 100 }}>
                        <select
                          style={selectStyle}
                          value={String(row.correo)}
                          disabled={disabled}
                          onChange={e => {
                            const n = Number(e.target.value);
                            updateRowByContactoId(contactoId, {
                              correo: n === 2 ? CORREO_CONTACTO_SECUNDARIO : CORREO_CONTACTO_PRIMARIO,
                            });
                          }}
                          aria-label="Correo"
                        >
                          {CORREO_OPTIONS.map(o => (
                            <option key={o.value} value={String(o.value)}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ minWidth: 72 }}>
                        <select
                          style={selectStyle}
                          value={row.repDocs}
                          disabled={disabled}
                          onChange={e =>
                            updateRowByContactoId(contactoId, {
                              repDocs: e.target.value as ProveedorContactoSiNo,
                            })
                          }
                          aria-label="Rep. Docs"
                        >
                          {SI_NO_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ minWidth: 88 }}>
                        <select
                          style={selectStyle}
                          value={row.recordatorio}
                          disabled={disabled}
                          onChange={e =>
                            updateRowByContactoId(contactoId, {
                              recordatorio: e.target.value as ProveedorContactoSiNo,
                            })
                          }
                          aria-label="Recordatorio"
                        >
                          {SI_NO_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-sm btn-light border"
                          style={{ padding: '2px 6px', lineHeight: 1 }}
                          aria-label="Quitar contacto"
                          disabled={disabled}
                          onClick={() => handleRemoveRow(contactoId)}
                        >
                          <FaTimes size={11} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
